import { toast } from "sonner";
import { api } from "@/lib/api";
import { clearCache, ensureScope, meta, rows, setMeta } from "./db";
import { conflictList, flush, pendingCount } from "./queue";
import { getActiveWorkspaceId } from "@/lib/workspace";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ATTACHMENT_CACHE = "diary-attachments-v1";
const STALE_MS = 6 * 60 * 60 * 1000;

const state = {
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  pending: 0,
  conflicts: 0,
  syncing: false,
  caching: false,
  progress: null,
  lastSyncAt: null,
  cachedAt: null,
  counts: {},
};

const subs = new Set();

export const getOfflineState = () => ({ ...state });

export const subscribeOffline = (fn) => {
  subs.add(fn);
  fn({ ...state });
  return () => subs.delete(fn);
};

const emit = (patch = {}) => {
  Object.assign(state, patch);
  const snap = { ...state };
  subs.forEach((fn) => fn(snap));
};

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const shiftMonths = (months) => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return iso(d);
};

const shiftDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return iso(d);
};

const weekStartIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return iso(d);
};

export const refreshOfflineCounts = async () => {
  emit({
    pending: await pendingCount(),
    conflicts: (await conflictList()).length,
    cachedAt: await meta("cached_at"),
    counts: {
      events: (await rows("events")).length,
      matters: (await rows("matters")).length,
      todos: (await rows("todos")).length,
      postits: (await rows("postits")).length,
      logs: (await rows("logs")).length,
    },
  });
};

export const flushNow = async ({ silent = false } = {}) => {
  if (!navigator.onLine || state.syncing) return null;
  if (!(await pendingCount())) {
    await refreshOfflineCounts();
    return null;
  }
  emit({ syncing: true });
  const result = await flush();
  emit({ syncing: false, lastSyncAt: new Date().toISOString() });
  await refreshOfflineCounts();
  if (result?.applied) {
    window.dispatchEvent(new Event("diary-refresh"));
    if (!silent) {
      toast.success(`${result.applied} offline change${result.applied === 1 ? "" : "s"} saved to the diary`);
    }
  }
  if (result?.conflicts) {
    toast.warning(
      `${result.conflicts} change${result.conflicts === 1 ? "" : "s"} clashed with the diary — choose which version to keep`,
    );
  }
  (result?.failed || []).forEach((line) => toast.error(line));
  return result;
};

const cacheAttachments = async (days) => {
  if (!days || !("caches" in window)) return;
  const until = shiftDays(days);
  const today = iso(new Date());
  const nearMatters = new Set(
    (await rows("events"))
      .filter((e) => e.matter_id && e.date >= today && e.date <= until)
      .map((e) => e.matter_id),
  );
  if (!nearMatters.size) return;
  const cache = await caches.open(ATTACHMENT_CACHE);
  const ws = getActiveWorkspaceId();
  const logs = (await rows("logs")).filter((l) => nearMatters.has(l.matter_id));
  for (const log of logs) {
    for (const att of log.attachments || []) {
      if (!att.attachment_id || String(att.attachment_id).startsWith("local_")) continue;
      const url = `${API}/attachments/${att.attachment_id}/download${ws ? `?workspace_id=${ws}` : ""}`;
      try {
        const res = await fetch(url, { credentials: "include" });
        if (res.ok) await cache.put(url, res.clone());
      } catch {
        /* skip anything that will not download */
      }
    }
  }
};

/** Pulls the configured window of the diary into IndexedDB for offline use. */
export const cacheNow = async (prefs = {}, { silent = false } = {}) => {
  if (!navigator.onLine) {
    if (!silent) toast.error("You're offline — connect once to refresh the offline copy");
    return;
  }
  if (state.caching) return;
  const back = Number(prefs.offline_months_back ?? 3);
  const ahead = Number(prefs.offline_months_ahead ?? 6);
  const attachDays = Number(prefs.offline_attachment_days ?? 21);
  const start = shiftMonths(-back);
  const end = shiftMonths(ahead);
  const today = iso(new Date());
  const week = weekStartIso();

  const jobs = [
    ["Your account", () => api.get("/auth/me")],
    ["Diaries", () => api.get("/workspaces")],
    ["Courts", () => api.get("/courts")],
    ["Matters", () => api.get("/matters")],
    ["Diary entries", () => api.get("/events", { params: { start, end } })],
    ["To-dos", () => api.get("/todos", { params: { start, end } })],
    ["Post-its", () => api.get("/postits")],
    ["Post-it styles", () => api.get("/postits/meta")],
    ["Hearing logs", () => api.get("/logs")],
    ["Unlinked entries", () => api.get("/events/uncategorized", { params: { include_past: true } })],
    ["Dashboard", () => api.get("/dashboard/summary")],
    ["Heat map", () => api.get("/calendar/heatmap", { params: { start: week, end: shiftDays(60) } })],
    ["To-do badges", () => api.get("/todos/summary", { params: { start, end } })],
    ["Week overview", () => api.get("/overview", { params: { span: "week", date: today } })],
    ["Month overview", () => api.get("/overview", { params: { span: "month", date: today } })],
    ["Reminder settings", () => api.get("/reminders/settings")],
    ["Court day kits", () => api.get("/kit/range", { params: { start: today, end: shiftDays(Math.max(attachDays, 21)) } })],
    ["Dictated notes", () => api.get("/logs/review-queue")],
    ["Billing position", () => api.get("/billing/status")],
    ["Post-it intake", () => api.get("/inbound/status")],
    ["Notifications", () => api.get("/notifications")],
  ];

  emit({ caching: true, progress: { done: 0, total: jobs.length + 2, step: "Starting" } });
  let done = 0;
  for (const [label, run] of jobs) {
    emit({ progress: { done, total: jobs.length + 2, step: label } });
    try {
      await run();
    } catch {
      /* one missing slice should not stop the rest */
    }
    done += 1;
  }

  emit({ progress: { done, total: jobs.length + 2, step: "Matter files" } });
  try {
    const until = shiftDays(attachDays || 21);
    const nearby = [
      ...new Set(
        (await rows("events"))
          .filter((e) => e.matter_id && e.date >= today && e.date <= until)
          .map((e) => e.matter_id),
      ),
    ].slice(0, 60);
    for (const matterId of nearby) {
      try {
        await api.get(`/matters/${matterId}/diary`);
      } catch {
        /* skip */
      }
    }
  } catch {
    /* ignore */
  }
  done += 1;

  emit({ progress: { done, total: jobs.length + 2, step: "Orders and attachments" } });
  await cacheAttachments(attachDays);
  done += 1;

  const at = new Date().toISOString();
  await setMeta("cached_at", at);
  emit({ caching: false, progress: null, cachedAt: at });
  await refreshOfflineCounts();
  if (!silent) toast.success("The diary is saved on this device for offline use");
};

export const clearOfflineCopy = async () => {
  await clearCache();
  await refreshOfflineCounts();
  toast.success("Offline copy cleared");
};

let started = false;
let lastPrefs = {};

export const initOffline = async ({ workspaceId, prefs = {} } = {}) => {
  lastPrefs = prefs;
  await ensureScope(workspaceId);
  await refreshOfflineCounts();
  emit({ online: navigator.onLine });

  if (!started) {
    started = true;
    window.addEventListener("online", () => {
      emit({ online: true });
      flushNow();
    });
    window.addEventListener("offline", () => emit({ online: false }));
    window.addEventListener("offline-queued", () => refreshOfflineCounts());
    setInterval(() => {
      if (navigator.onLine && state.pending && !state.syncing) flushNow({ silent: true });
    }, 45000);
  }

  if (navigator.onLine) {
    await flushNow({ silent: true });
    if (prefs.offline_enabled !== false) {
      const cachedAt = await meta("cached_at");
      const stale = !cachedAt || Date.now() - new Date(cachedAt).getTime() > STALE_MS;
      if (stale) cacheNow(prefs, { silent: true });
    }
  }
};

export const cacheWithSavedPrefs = (options) => cacheNow(lastPrefs, options);
